/**
 * Unified variant system for action cards and entity displays.
 *
 * Standardises the visual treatment (background, border, text) across all
 * proposal/action card components. Every map includes dark-mode classes so
 * cards render correctly in both themes.
 *
 * Pick the map that matches your component's role:
 *
 *   VARIANT_CARD       inner entity cards (bg-secondary default)
 *   VARIANT_SHELL      outer full-card wrappers (bg-primary default)
 *   VARIANT_BORDER     border-only override (for shells that manage their own bg)
 *   VARIANT_HEADER_BG  header / section tinting
 *   VARIANT_TEXT        label / badge text color
 */

/** The three visual variants used across action and entity cards. */
export type ActionVariant = 'default' | 'destructive' | 'warning';

/** Full bg + border for inner entity cards (bg-secondary default). */
export const VARIANT_CARD: Record<ActionVariant, string> = {
  default: 'bg-bg-secondary border-border-secondary',
  destructive:
    'bg-utility-error-50 border-utility-error-200 dark:bg-error-950 dark:border-error-800',
  warning:
    'bg-utility-warning-50 border-utility-warning-200 dark:bg-warning-950 dark:border-warning-800',
};

/** Full bg + border for outer shell cards (bg-primary default). */
export const VARIANT_SHELL: Record<ActionVariant, string> = {
  default: 'bg-bg-primary border-border-secondary',
  destructive:
    'bg-error-50 border-error-200 dark:bg-error-950 dark:border-error-700',
  warning:
    'bg-warning-50 border-warning-200 dark:bg-warning-950 dark:border-warning-700',
};

/** Border-only overrides for components that manage their own background. */
export const VARIANT_BORDER: Record<ActionVariant, string> = {
  default: '',
  destructive: 'border-error-200 dark:border-error-700',
  warning: 'border-warning-200 dark:border-warning-700',
};

/** Header / section background tinting. */
export const VARIANT_HEADER_BG: Record<ActionVariant, string> = {
  default: '',
  destructive: 'bg-error-50 dark:bg-error-950',
  warning: 'bg-warning-50 dark:bg-warning-950',
};

/** Text color for variant-specific labels and badges. */
export const VARIANT_TEXT: Record<ActionVariant, string> = {
  default: 'text-fg-secondary',
  destructive: 'text-error-600 dark:text-error-400',
  warning: 'text-warning-600 dark:text-warning-400',
};
